import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { userEndpoints, usersRolesEndpoints, departmentsEndpoints } from '@/api/endpoints';
import { callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import {
  Department,
  DepartmentUser,
  DepartmentUsers,
  Departments,
} from '@/modules/departments/models';
import { User } from '@/shared/types/user';

interface AddMembersProps {
  department_id: string;
  user_ids: string[];
}

export const API = Object.freeze({
  listDepartments: async (deskId: string): Promise<Departments> =>
    callGet(departmentsEndpoints.departments(deskId)),
  listDepartmentUsers: async (deskId: string, departmentId: string): Promise<DepartmentUsers> =>
    callGet(departmentsEndpoints.departmentUsers(deskId, departmentId)),

  getDepartment: async (deskId: string, departmentId: string): Promise<Department> =>
    callGet(departmentsEndpoints.department(deskId, departmentId)),

  createDepartment: async (
    deskId: string,
    newDepartment: Partial<Department>
  ): Promise<Department> => callPost(departmentsEndpoints.departments(deskId), newDepartment),

  updateDepartment: async (deskId: string, department: Partial<Department>): Promise<Department> =>
    callPut(departmentsEndpoints.department(deskId, department.department_id), department),

  deleteDepartment: async (deskId: string, departmentId: string): Promise<Department> =>
    callDelete(departmentsEndpoints.department(deskId, departmentId)),

  addMembers: async (
    deskId: string,
    departmentId: string,
    user_ids: string[]
  ): Promise<DepartmentUsers> =>
    callPost(departmentsEndpoints.departmentUsers(deskId, departmentId), { user_ids }),

  removeMember: async (
    deskId: string,
    departmentId: string,
    user_id: string
  ): Promise<DepartmentUser> =>
    callDelete(departmentsEndpoints.departmentUser(deskId, departmentId, user_id)),
});

export const onDepartmentUpdated = (
  queryClient: QueryClient,
  deskId: string,
  department: Department
) => {
  queryClient.setQueryData<Department>(
    [departmentsEndpoints.department(deskId, department.department_id)],
    (prev: Department) => ({ ...prev, ...department })
  );

  const queryKey = [departmentsEndpoints.departments(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Departments>(queryKey, (prev: Departments) => ({
      departments: (prev?.departments || []).map((item) =>
        item.department_id === department.department_id ? { ...item, ...department } : item
      ),
    }));
  }

  const user = queryClient.getQueryData<User>([userEndpoints.user]);
  const key = usersRolesEndpoints.userDepartments(user.user_id);

  if (key) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
};

export const onDepartmentCreated = (
  queryClient: QueryClient,
  deskId: string,
  department: Department
) => {
  const queryKey = [departmentsEndpoints.departments(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Departments>(queryKey, (prev) => ({
      departments: [
        ...(prev?.departments || []).filter(
          (item) => item.department_id !== department.department_id
        ),
        department,
      ],
    }));
  }
};

export const onDepartmentRemoved = (
  queryClient: QueryClient,
  deskId: string,
  department_id: string
) => {
  const queryKey = [departmentsEndpoints.departments(deskId)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<Departments>(queryKey, (prev: Departments) => ({
      departments: (prev?.departments || []).filter((acc) => acc.department_id !== department_id),
    }));
  }
};

export const onMembersAdded = (
  queryClient: QueryClient,
  deskId: string,
  department_id: string,
  data: DepartmentUsers
) => {
  const queryKey = [departmentsEndpoints.departmentUsers(deskId, department_id)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<DepartmentUsers>(queryKey, (prev) => ({
      users: [...(prev?.users || []), ...data.users],
    }));
  }
};

export const onMemberRemoved = (
  queryClient: QueryClient,
  deskId: string,
  data: { department_id: string; user_id: string }
) => {
  const queryKey = [departmentsEndpoints.departmentUsers(deskId, data.department_id)];
  if (queryClient.getQueryData(queryKey)) {
    queryClient.setQueryData<DepartmentUsers>(queryKey, (prev: DepartmentUsers) => {
      return { users: (prev?.users || []).filter((acc) => acc.user_id !== data.user_id) };
    });
  }
};

export const useDepartments = (deskId: string, departmentId?: string) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data: departments, status: listStatus } = useQuery<Departments, Error>({
    queryKey: [departmentsEndpoints.departments(deskId)],
    queryFn: () => API.listDepartments(deskId),
    enabled: !!deskId,
  });

  const { data: department, status: getStatus } = useQuery<Department, Error>({
    queryKey: [departmentsEndpoints.department(deskId, departmentId)],
    queryFn: () => API.getDepartment(deskId, departmentId),
    enabled: !!departmentId && !!deskId,
  });

  const { data: departmentUsers } = useQuery<DepartmentUsers, Error>({
    queryKey: [departmentsEndpoints.departmentUsers(deskId, departmentId)],
    queryFn: () => API.listDepartmentUsers(deskId, departmentId),
    enabled: !!departmentId && !!deskId,
  });

  const { mutate: updateDepartment, status: updateStatus } = useMutation<
    Department,
    Error,
    Partial<Department>
  >({
    mutationFn: (department) => API.updateDepartment(deskId, department),
    onSuccess: (resp) => {
      onDepartmentUpdated(queryClient, deskId, resp);
      toast.success(t('departments.updated', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: createDepartment, status: createStatus } = useMutation<
    Department,
    Error,
    Partial<Department>
  >({
    mutationFn: (department) => API.createDepartment(deskId, department),
    onSuccess: (resp) => {
      onDepartmentCreated(queryClient, deskId, resp);
      toast.success(t('departments.created', { 0: resp.name }));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteDepartment, status: deleteStatus } = useMutation<Department, Error, string>(
    {
      mutationFn: (id) => API.deleteDepartment(deskId, id),
      onSuccess: (resp) => {
        onDepartmentRemoved(queryClient, deskId, resp.department_id);
        toast.success(t('departments.deleted'));
      },
      meta: { errorToast: true },
    }
  );

  const { mutate: addMembersToDepartment, status: addMemberStatus } = useMutation<
    DepartmentUsers,
    Error,
    AddMembersProps
  >({
    mutationFn: (data) => API.addMembers(deskId, data.department_id, data.user_ids),
    onSuccess: (resp, variables) => {
      onMembersAdded(queryClient, deskId, variables.department_id, resp);
    },
    meta: { errorToast: true },
  });

  const { mutate: removeMemberFromDepartment } = useMutation<DepartmentUser, Error, DepartmentUser>(
    {
      mutationFn: (data) => API.removeMember(deskId, data.department_id, data.user_id),
      onSuccess: (resp) => {
        onMemberRemoved(queryClient, deskId, resp);
      },
      meta: { errorToast: true },
    }
  );

  return {
    departments: departments?.departments,
    departmentUsers: departmentUsers?.users,
    department,
    getStatus,
    listStatus,
    updateDepartment,
    updateStatus,
    createDepartment,
    createStatus,
    deleteDepartment,
    deleteStatus,
    addMembersToDepartment,
    addMemberStatus,
    removeMemberFromDepartment,
  };
};
